import { FormattedMessage, useIntl } from "react-intl";
import { Heading, Box, Grid, AlertIcon, Alert, Button } from "@chakra-ui/react";
import { FullPageSpinner } from "components/Spinner";
import PaymentMethod from "components/PaymentMethodSelector/PaymentMethod";
import { useGetTransactionMethods } from "api/transaction-methods";
import { AddPaymentMethodButton } from "components/AddPaymentMethod";

export const filterTransactionMethodsByCapability = (getTransactionMethodsPayload = {}, capability, removeWallets = true) => {
  const keysToFilterOn = getTransactionMethodsPayload.capabilities[capability];
  return Object.values(getTransactionMethodsPayload.payment_methods || {}).filter(pm => {
    const isWallet = pm.provider === "wallet";
    const shouldNotBeIncludedInResults = removeWallets && isWallet;
    return keysToFilterOn.includes(pm.id) && !shouldNotBeIncludedInResults;
  });
};

const PurchasePMList = ({ data, onRefetch, onAddPaymentMethodClick }) => {
  const { formatMessage } = useIntl();
  const purchasePMs = filterTransactionMethodsByCapability(data.payload, "purchase");
  const processedPurchasePMs = purchasePMs.map(pm => {
    const isDefault = pm.tags.some(
      (tag) => tag.tag === "default_payment" && tag.status === "ACTIVE"
    );
    return { ...pm, isDefault }; // need to spread original object so isDefault doesn't get overwriten in other context
  });
  return (
    <Grid gridGap="4" data-testid="purchase-payment-method-list">
      {processedPurchasePMs.map((pm) => (
        <PaymentMethod
          isSelected={false}
          key={`payment-method-${pm.id}`}
          paymentMethod={pm}
          onSelect={() => {}}
          onPayoutOptionSelect={() => {}}
          onUpdate={onRefetch}
          showMenu={true}
          disabled={false}
          isPayout={false}
        />
      ))}
      <AddPaymentMethodButton
        dataTestId="add-payment-method"
        buttonText={formatMessage({
          defaultMessage: "Add payment method",
          description: "Add payment method button text",
        })}
        onOpen={() => {
          onAddPaymentMethodClick({
            chooserConfig: {
              include: ["paypal"],
              exclude: [],
              addCC: true,
              addPaypal: true,
              addACH: false,
            }
          });
        }}
      />
    </Grid>
  );
};

const PayoutPMList = ({ data, onRefetch, onAddPaymentMethodClick }) => {
  const { formatMessage } = useIntl();
  const payoutPMs = filterTransactionMethodsByCapability(data.payload, "payout");
  const processedPayoutPMs = payoutPMs.filter((pm) => {
    return pm.method_class !== "banking"; // remove any payoutv1 payout methods (which were a combination of a bank account and a Masspay payout rail)
  }).map(pm => {
    const isDefault = pm.tags.some(
      (tag) => tag.tag === "default_payout" && tag.status === "ACTIVE"
    );
    return { ...pm, isDefault }; // need to spread original object so isDefault doesn't get overwriten in other context
  });
  return (
    <Grid gridGap="4" data-testid="payout-payment-method-list">
      {processedPayoutPMs.map((pm) => (
        <PaymentMethod
          isSelected={false}
          key={`payment-method-${pm.id}`}
          paymentMethod={pm}
          onSelect={() => {}}
          onPayoutOptionSelect={() => {}}
          onUpdate={onRefetch}
          showMenu={true}
          disabled={false}
          isPayout={true}
        />
      ))}
      <AddPaymentMethodButton
        dataTestId="add-payout-method-button"
        onOpen={() => {
          onAddPaymentMethodClick({
            chooserConfig: {
              include: ["paypal"],
              exclude: [],
              addCC: false,
              addPaypal: true,
              addACH: false,
            }
          });
        }}
        buttonText={formatMessage({
          defaultMessage: "Add payout method",
          description: "Add payout method button text",
        })}
      />
    </Grid>
  );
};

const PMBreakdown = ({ onAddPaymentMethodClick, onCloseButtonClicked, hideExitButtons = false }) => {
  const {
    data: transactionMethodsData,
    isLoading: isLoadingGetTransactionMethods,
    isError: isGetTransactionMethodsError,
    refetch: refetchTransactionMethods,
  } = useGetTransactionMethods({ bank_accounts: true }, { gcTime: 0 });

  let content;
  if (isLoadingGetTransactionMethods) {
    content = <FullPageSpinner />;
  } else if (isGetTransactionMethodsError) {
    content = (
      <Alert status='error'>
        <AlertIcon />
        <FormattedMessage defaultMessage="There was an error retrieving transaction method details" description="Section header" />
      </Alert>
    );
  } else {
    content = (
      <>
        <Box pt={4} pb={6}>
          <Heading mb={4} color="secondaryText">
            <FormattedMessage defaultMessage="Payment Methods" description="Section header" />
          </Heading>
          <PurchasePMList
            data={transactionMethodsData}
            onRefetch={refetchTransactionMethods}
            onAddPaymentMethodClick={onAddPaymentMethodClick}
          />
        </Box>
        <Box pt={4} pb={6}>
          <Heading mb={4} color="secondaryText">
            <FormattedMessage defaultMessage="Payout Methods" description="Section header" />
          </Heading>
          <PayoutPMList
            data={transactionMethodsData}
            onRefetch={refetchTransactionMethods}
            onAddPaymentMethodClick={onAddPaymentMethodClick}
          />
        </Box>
        {!hideExitButtons && (
          <Button mb="4" onClick={onCloseButtonClicked} width="full">
            <FormattedMessage defaultMessage="Done" description="Button label" />
          </Button>
        )}
      </>
    );
  }

  return content;
};

export default PMBreakdown;
